import { Icon, Pill } from "@/components/ui";
// States.tsx — shared loading / error / empty inline states for data screens.
import type { ReactNode } from "react";

// Inline loading indicator — centered spinner + Korean copy.
export const LoadingState = ({ label = "불러오는 중…" }: { label?: string }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 14,
      padding: "72px 0",
      color: "var(--text-dim)",
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        border: "3px solid var(--bg-card)",
        borderTopColor: "var(--brand)",
        animation: "spin 0.8s linear infinite",
      }}
    />
    <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    <span style={{ fontSize: 13.5 }}>{label}</span>
  </div>
);

// Inline error state with an optional retry handler.
export const ErrorState = ({ error, onRetry }: { error: Error; onRetry?: () => void }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 14,
      padding: "64px 0",
      color: "var(--text-negative)",
    }}
  >
    <Icon name="alert" size={28} color="var(--text-negative)" />
    <div style={{ fontSize: 14, fontWeight: 700 }}>데이터를 불러오지 못했습니다</div>
    <div style={{ fontSize: 12.5, color: "var(--text-dim)", maxWidth: 420, textAlign: "center" }}>
      {error.message}
    </div>
    {onRetry && (
      <Pill variant="outlined" size="sm" icon={<Icon name="history" size={15} />} onClick={onRetry}>
        다시 시도
      </Pill>
    )}
  </div>
);

// Wraps a screen body: shows loading/error first, otherwise children.
export const DataBoundary = ({
  loading,
  error,
  onRetry,
  children,
}: {
  loading: boolean;
  error: Error | null;
  onRetry?: () => void;
  children: ReactNode;
}) => {
  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  return <>{children}</>;
};
